'use client';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../ui/button';

import {
  readMessageSoundSettings,
  resolveMessageSoundUrl,
} from '@/app/hooks/useMessageSoundSettings';

import { setUnreadMode, setUnreadTotal } from '@/app/lib/unread/unread-store';

type UnreadCounts = {
  messages?: number;
  notifications?: number;
  total?: number;
  updatedAt?: number;
};

export function UnreadMessagesNotifier() {
  const { status } = useSession();
  const router = useRouter();

  const lastTotalRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') {
      setUnreadMode('off');
      setUnreadTotal(0);
      lastTotalRef.current = null;
      return;
    }

    let alive = true;

    const syncOnce = async () => {
      try {
        // показываем хотя бы poll, пока SSE не открыт
        setUnreadMode('poll');

        const res = await fetch('/api/user/unread-count', { cache: 'no-store' });
        const data = await res.json();

        const total =
          typeof data?.total === 'number'
            ? data.total
            : typeof data?.count === 'number'
              ? data.count
              : 0;

        if (!alive) return;
        setUnreadTotal(total);
        lastTotalRef.current = total;
      } catch {
        // ignore
      }
    };

    const playSound = () => {
      const settings = readMessageSoundSettings();
      const src = resolveMessageSoundUrl(settings);
      if (!src) return;

      if (!audioRef.current || audioRef.current.src !== new URL(src, window.location.href).href) {
        audioRef.current = new Audio(src);
      }

      audioRef.current.currentTime = 0;
      void audioRef.current.play().catch(() => null);
    };

    // 1) initial sync
    void syncOnce();

    // 2) SSE
    const es = new EventSource('/api/unread/events');

    es.onopen = () => {
      setUnreadMode('sse');
    };

    const onCounts = (ev: MessageEvent) => {
      try {
        const payload = JSON.parse(ev.data) as UnreadCounts;
        const total =
          typeof payload.total === 'number'
            ? payload.total
            : (payload.messages ?? 0) + (payload.notifications ?? 0);

        const prev = lastTotalRef.current;
        lastTotalRef.current = total;

        setUnreadTotal(total);

        // звук/тост только если уже было значение и стало больше
        if (prev !== null && total > prev) {
          playSound();
        }

        // (опционально) тост как у тебя был
        if (prev !== null && total > prev) {
          toast.custom(
            (t) => (
              <div
                className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-slate-800 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
              >
                <div className="flex-1 w-0 p-4">
                  <p className="font-medium text-white">
                    У вас <span className="font-bold">{total}</span> новых уведомлений!
                  </p>
                </div>
                <div className="flex border-l border-slate-700">
                  <Button
                    variant="ghost"
                    className="w-full text-white hover:bg-slate-700"
                    onClick={() => {
                      toast.dismiss(t.id);
                      router.push('/profile/messages');
                    }}
                  >
                    Прочитать
                  </Button>
                </div>
              </div>
            ),
            { id: 'unread-toast', duration: 10_000 },
          );
        } else if (total === 0) {
          toast.dismiss('unread-toast');
        }
      } catch {
        // ignore
      }
    };

    es.addEventListener('counts', onCounts as EventListener);

    es.onerror = () => {
      // ВАЖНО: не закрываем — EventSource сам переподключится
      setUnreadMode('poll');
    };

    // 3) poll fallback раз в минуту (на случай проблем с SSE)
    const pollId = window.setInterval(() => {
      void syncOnce();
    }, 60_000);

    return () => {
      alive = false;
      window.clearInterval(pollId);
      es.removeEventListener('counts', onCounts as EventListener);
      es.close();
    };
  }, [status, router]);

  return null;
}